import { useCallback, useRef } from 'react';
import type { SupportMeetingWorkflow, SupportMeetingWorkflowResult } from '@web-front/support-chat';
import { consultationService, baseUrlV1 } from '../../configs/api/api.config';
import { consultationsApi } from '../../services/consultations.api';
import { useAppDispatch } from '../../hooks/redux';

type Intent = Parameters<SupportMeetingWorkflow['prepare']>[0];
type Client = { id: number; full_name: string; time_zone?: string | null; is_group?: boolean };
type Consultation = {
  id: number;
  date: string;
  datetime_start: string;
  datetime_end: string;
  consultation_type?: 'online' | 'offline';
  client_entity?: string | null;
  link?: string | null;
  series_id?: string | null;
};
type Scope = 'single' | 'this_and_future' | 'series';
type Draft = {
  intent: Intent;
  client?: Client;
  consultation?: Consultation;
  needsClientCreate?: boolean;
  scope?: Scope;
};
type Collection<T> = T[] | { items?: T[]; 'hydra:member'?: T[] };

class SupportWorkflowRequestError extends Error {
  constructor(readonly status: number) {
    super('Support workflow request failed with status ' + status);
  }
}

/**
 * The widget receives only a validated intent. Client records, IDs, JWT and all
 * mutations stay inside this host-browser adapter.
 */
export function useSupportMeetingWorkflow(
  token: string | null,
  consultationTimeZone: string,
  localDate: string,
): SupportMeetingWorkflow {
  const dispatch = useAppDispatch();
  const drafts = useRef(new Map<string, Draft>());
  const latestCreatedMeeting = useRef<{ link: string; clientName: string } | null>(null);

  const request = useCallback(
    async <T>(url: string, init?: RequestInit): Promise<T> => {
      const response = await fetch(url, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: 'Bearer ' + token } : {}),
          ...(init?.headers || {}),
        },
      });
      if (!response.ok) throw new SupportWorkflowRequestError(response.status);
      return response.status === 204 ? (undefined as T) : (response.json() as Promise<T>);
    },
    [token],
  );

  const newDraftId = () => globalThis.crypto?.randomUUID?.() || 'meeting-' + Date.now() + '-' + Math.random();
  const clientsForName = useCallback(
    async (name: string) => {
      const query = new URLSearchParams({ full_name: name, is_active: 'true', itemsPerPage: '20' });
      return items(await request<Collection<Client>>(baseUrlV1 + '/clients?' + query));
    },
    [request],
  );
  const consultationsForClient = useCallback(
    async (clientId: number) => {
      const query = new URLSearchParams({ client_entity: '/api/v1/clients/' + clientId, consultation_type: 'online' });
      return items(await request<Collection<Consultation>>(consultationService + '/consultations?' + query)).filter(
        (item) => item.consultation_type !== 'offline',
      );
    },
    [request],
  );

  const preview = useCallback(
    async (draftId: string): Promise<SupportMeetingWorkflowResult> => {
      const draft = drafts.current.get(draftId);
      if (!draft?.client) return info('Не удалось определить клиента. Уточните, пожалуйста, его имя.');
      if (draft.intent.operation === 'delete') {
        if (!draft.consultation) return info('Не удалось определить встречу для удаления.');
        return confirm(
          draftId,
          'Удалить встречу',
          'Удалить онлайн-встречу с клиентом ' +
            draft.client.full_name +
            ': ' +
            formatTime(draft.consultation.datetime_start) +
            '? Это действие нельзя отменить.',
        );
      }
      if (draft.intent.operation !== 'create' && draft.intent.operation !== 'reschedule')
        return info('Этот запрос не требует подтверждения.');
      const { date, startTime, durationMinutes } = draft.intent;
      if (!date || !startTime || !durationMinutes) return info('Не хватает даты, времени или длительности встречи.');
      const endTime = plusMinutes(startTime, durationMinutes);
      const payload = consultationPayload(
        draft.client,
        date,
        startTime,
        endTime,
        consultationTimeZone,
        draft.consultation?.id,
        draft.intent.operation === 'create' ? draft.intent.recurrence : undefined,
      );
      try {
        const checked = await request<{
          datetime_start?: string;
          recurrence?: { count: number; first_start: string; last_start: string };
        }>(consultationService + '/consultations/availability', { method: 'POST', body: JSON.stringify(payload) });
        const verb = draft.intent.operation === 'create' ? 'Создать встречу' : 'Перенести встречу';
        const yourTime =
          formatTime(checked.datetime_start, consultationTimeZone) || date + ', ' + startTime + '–' + endTime;
        const clientTime = formatClientTime(checked.datetime_start, draft.client.time_zone, consultationTimeZone);
        const original = draft.consultation
          ? ' Исходная встреча: ' + formatTime(draft.consultation.datetime_start, consultationTimeZone) + '.'
          : '';
        const recurrence = checked.recurrence
          ? ' Серия: ' +
            recurrenceSummary(
              draft.intent.recurrence,
              checked.recurrence.count,
              checked.recurrence.first_start,
              checked.recurrence.last_start,
              consultationTimeZone,
            ) +
            '.'
          : '';
        const scope = draft.scope ? ' Охват: ' + scopeLabel(draft.scope) + '.' : '';
        return confirm(
          draftId,
          verb,
          verb +
            ' онлайн с клиентом ' +
            draft.client.full_name +
            '. Ваше время: ' +
            yourTime +
            (clientTime ? '. Время клиента: ' + clientTime : '') +
            '.' +
            original +
            recurrence +
            scope +
            ' Подтвердите действие.',
        );
      } catch (error) {
        return info(availabilityErrorMessage(error));
      }
    },
    [consultationTimeZone, request],
  );

  const pickConsultation = useCallback(
    async (draftId: string, client: Client): Promise<SupportMeetingWorkflowResult> => {
      const draft = drafts.current.get(draftId);
      if (!draft) return info('Черновик действия устарел. Повторите запрос.');
      draft.client = client;
      const requestedDate = draft.intent.operation === 'reschedule' ? draft.intent.currentDate : draft.intent.date;
      const matches = (await consultationsForClient(client.id)).filter(
        (item) => !requestedDate || item.date.slice(0, 10) === requestedDate,
      );
      if (matches.length === 0) return info('Не нашёл подходящую онлайн-встречу. Уточните дату встречи.');
      if (matches.length > 1) {
        return {
          kind: 'selection',
          draftId,
          message:
            draft.intent.operation === 'delete'
              ? 'Уточните, какую встречу удалить:'
              : 'Уточните, какую встречу нужно перенести:',
          choices: matches.map((item) => ({
            id: 'consultation:' + item.id,
            label: formatTime(item.datetime_start) || item.date,
          })),
        };
      }
      draft.consultation = matches[0];
      return selectSeriesScopeOrPreview(draftId, draft, preview);
    },
    [consultationsForClient, preview],
  );

  const listClients = useCallback(async (): Promise<SupportMeetingWorkflowResult> => {
    try {
      const query = new URLSearchParams({ is_active: 'true', itemsPerPage: '50', 'sort[full_name]': 'ASC' });
      const clients = items(await request<Collection<Client>>(baseUrlV1 + '/clients?' + query));
      return {
        kind: 'list',
        title: 'Активные клиенты',
        items: clients.map((client) => ({
          id: String(client.id),
          title: client.full_name,
          description: client.is_group ? 'Группа' : 'Клиент',
          entityKind: client.is_group ? 'group' : 'client',
        })),
      };
    } catch {
      return info('Не удалось получить список клиентов. Попробуйте ещё раз.');
    }
  }, [request]);

  const listConsultations = useCallback(
    async (range: 'today' | 'week'): Promise<SupportMeetingWorkflowResult> => {
      const endDate = range === 'week' ? addDays(localDate, 6) : localDate;
      const query = new URLSearchParams({
        consultation_type: 'online',
        'date[after]': localDate,
        'date[before]': endDate,
        'sort[datetime_start]': 'asc',
      });
      try {
        const [consultations, clients] = await Promise.all([
          request<Collection<Consultation>>(consultationService + '/consultations?' + query),
          request<Collection<Client>>(
            baseUrlV1 + '/clients?' + new URLSearchParams({ is_active: 'true', itemsPerPage: '500' }),
          ),
        ]);
        const names = new Map(items(clients).map((client) => [String(client.id), client.full_name]));
        return {
          kind: 'list',
          title: range === 'today' ? 'Встречи на сегодня' : 'Встречи на неделю',
          items: items(consultations)
            .filter((item) => item.consultation_type !== 'offline')
            .map((item) => ({
              id: String(item.id),
              title: formatTime(item.datetime_start) || item.date,
              description: names.get(clientId(item.client_entity)) || 'Клиент',
              entityKind: 'consultation',
              ...(meetingLink(item.link)
                ? {
                    href: meetingLink(item.link),
                    invitationText: invitationText(
                      formatTime(item.datetime_start) || item.date,
                      meetingLink(item.link)!,
                    ),
                  }
                : {}),
            })),
        };
      } catch {
        return info('Не удалось получить список встреч. Попробуйте ещё раз.');
      }
    },
    [localDate, request],
  );

  const prepareLinkedReschedule = useCallback(
    async (intent: Intent, meetingCode: string | undefined): Promise<SupportMeetingWorkflowResult> => {
      if (!meetingCode)
        return info('Не вижу ссылку на встречу. Отправьте её ещё раз и укажите, на сколько минут перенести встречу.');
      try {
        const matches = items(
          await request<Collection<Consultation>>(
            consultationService + '/consultations?' + new URLSearchParams({ code: meetingCode, limit: '1' }),
          ),
        );
        const consultation = matches[0];
        const linkedClientId = clientId(consultation?.client_entity);
        if (!consultation || !linkedClientId)
          return info('Не нашёл доступную встречу по этой ссылке. Проверьте ссылку и повторите запрос.');
        const client = await request<Client>(baseUrlV1 + '/clients/' + encodeURIComponent(linkedClientId));
        const currentStart = scheduleDate(consultation.datetime_start);
        const currentEnd = scheduleDate(consultation.datetime_end);
        if (!currentStart || Number.isNaN(currentStart.getTime()))
          return info('Не удалось определить время этой встречи. Укажите дату и время вручную.');
        const shiftedStart = new Date(currentStart.getTime() + (intent.shiftMinutes || 0) * 60_000);
        const durationMinutes =
          currentEnd && !Number.isNaN(currentEnd.getTime())
            ? Math.max(15, Math.round((currentEnd.getTime() - currentStart.getTime()) / 60_000))
            : 60;
        const preparedIntent: Intent = {
          ...intent,
          clientName: client.full_name,
          date: schedulingDate(shiftedStart),
          startTime: schedulingTime(shiftedStart),
          durationMinutes,
          currentDate: schedulingDate(currentStart),
          currentStartTime: schedulingTime(currentStart),
        };
        const draftId = newDraftId();
        const draft: Draft = { intent: preparedIntent, client, consultation };
        drafts.current.set(draftId, draft);
        return selectSeriesScopeOrPreview(draftId, draft, preview);
      } catch {
        return info(
          'Не удалось проверить встречу по этой ссылке. Попробуйте ещё раз или укажите клиента, дату и время вручную.',
        );
      }
    },
    [preview, request],
  );

  const prepare = useCallback(
    async (intent: Intent, context?: { meetingCode?: string }): Promise<SupportMeetingWorkflowResult> => {
      if (intent.operation === 'join_recent') {
        const meeting = latestCreatedMeeting.current;
        if (!meeting) return info('Не вижу созданную в этом окне встречу. Откройте её из списка консультаций.');
        window.location.assign(meeting.link);
        return info('Открываю видеоконсультацию с клиентом «' + meeting.clientName + '».');
      }
      if (intent.operation === 'join_link') {
        if (!context?.meetingCode) return info('Не вижу кода встречи. Пришлите ссылку или пятисимвольный код ещё раз.');
        try {
          const matches = items(
            await request<Collection<Consultation>>(
              consultationService + '/consultations?' + new URLSearchParams({ code: context.meetingCode, limit: '1' }),
            ),
          );
          const link = meetingLink(matches[0]?.link);
          if (!link) return info('Не нашёл доступную встречу по этому коду. Проверьте ссылку или код.');
          window.location.assign(link);
          return info('Открываю видеоконсультацию.');
        } catch {
          return info('Не удалось открыть встречу по этому коду. Проверьте ссылку или попробуйте ещё раз.');
        }
      }
      if (intent.operation === 'list_clients') return listClients();
      if (intent.operation === 'list_consultations') return listConsultations(intent.range || 'today');
      if (intent.operation === 'reschedule' && intent.meetingReference === 'message_link') {
        return prepareLinkedReschedule(intent, context?.meetingCode);
      }
      if (!intent.clientName) return info('Уточните, пожалуйста, имя клиента.');
      const draftId = newDraftId();
      const draft: Draft = { intent };
      drafts.current.set(draftId, draft);
      try {
        const clients = await clientsForName(intent.clientName);
        const exact = clients.filter((client) => normalName(client.full_name) === normalName(intent.clientName || ''));
        if (intent.operation === 'create_client') {
          if (exact.length > 0) {
            drafts.current.delete(draftId);
            return info('Клиент «' + exact[0].full_name + '» уже есть в списке активных клиентов.');
          }
          draft.needsClientCreate = true;
          return confirm(draftId, 'Создать клиента', 'Создать карточку клиента «' + intent.clientName + '»?');
        }
        if (exact.length === 0) {
          if (intent.operation !== 'create') return info('Клиент «' + intent.clientName + '» не найден.');
          draft.needsClientCreate = true;
          return confirm(
            draftId,
            'Создать клиента',
            'Клиент «' +
              intent.clientName +
              '» не найден. Создать его карточку? После этого я ещё раз проверю время встречи.',
          );
        }
        if (exact.length === 1) draft.client = exact[0];
        else
          return {
            kind: 'selection',
            draftId,
            message: 'Нашёл несколько клиентов с таким именем. Выберите нужного:',
            choices: exact.map((client) => ({
              id: 'client:' + client.id,
              label: client.full_name,
              entityKind: client.is_group ? 'group' : 'client',
            })),
          };
        return intent.operation === 'create' ? preview(draftId) : pickConsultation(draftId, draft.client);
      } catch {
        return info('Не удалось проверить клиентов и расписание. Попробуйте ещё раз.');
      }
    },
    [clientsForName, listClients, listConsultations, pickConsultation, prepareLinkedReschedule, preview],
  );

  const select = useCallback(
    async (draftId: string, choiceId: string): Promise<SupportMeetingWorkflowResult> => {
      const draft = drafts.current.get(draftId);
      if (!draft) return info('Черновик действия устарел. Повторите запрос.');
      try {
        if (choiceId.startsWith('client:')) {
          const client = await request<Client>(baseUrlV1 + '/clients/' + encodeURIComponent(choiceId.slice(7)));
          if (draft.intent.operation === 'create') {
            draft.client = client;
            return preview(draftId);
          }
          return pickConsultation(draftId, client);
        }
        if (choiceId.startsWith('consultation:')) {
          draft.consultation = await request<Consultation>(
            consultationService + '/consultations/' + encodeURIComponent(choiceId.slice(13)),
          );
          return selectSeriesScopeOrPreview(draftId, draft, preview);
        }
        if (choiceId.startsWith('scope:')) {
          const scope = choiceId.slice(6) as Scope;
          if (!['single', 'this_and_future', 'series'].includes(scope))
            return info('Не удалось обработать выбранный охват серии.');
          draft.scope = scope;
          return preview(draftId);
        }
        return info('Не удалось обработать выбранный вариант.');
      } catch {
        return info('Не удалось получить выбранные данные.');
      }
    },
    [pickConsultation, preview, request],
  );

  const execute = useCallback(
    async (draftId: string): Promise<SupportMeetingWorkflowResult> => {
      const draft = drafts.current.get(draftId);
      if (!draft) return info('Черновик действия устарел. Повторите запрос.');
      if (draft.needsClientCreate) {
        try {
          const client = await request<Client>(baseUrlV1 + '/clients', {
            method: 'POST',
            body: JSON.stringify({
              full_name: draft.intent.clientName,
              time_zone: consultationTimeZone,
              is_active: true,
              is_group: false,
            }),
          });
          draft.needsClientCreate = false;
          draft.client = client;
          if (draft.intent.operation === 'create_client') {
            drafts.current.delete(draftId);
            return info('Карточка клиента «' + client.full_name + '» создана.');
          }
          return preview(draftId);
        } catch {
          return info('Не удалось создать карточку клиента. Проверьте имя и повторите попытку.');
        }
      }
      if (!draft.client || (!draft.consultation && draft.intent.operation !== 'create'))
        return info('Не выбрана встреча или клиент.');
      try {
        if (draft.intent.operation === 'delete' && draft.consultation) {
          await request(consultationService + '/consultations/' + draft.consultation.id, { method: 'GET' });
          const scopeQuery = draft.scope && draft.scope !== 'single' ? '?scope=' + draft.scope : '';
          await request(consultationService + '/consultations/' + draft.consultation.id + scopeQuery, {
            method: 'DELETE',
          });
          dispatch(consultationsApi.util.invalidateTags(['Consultations']));
          drafts.current.delete(draftId);
          return info('Встреча удалена.');
        }
        const { date, startTime, durationMinutes } = draft.intent;
        if (!date || !startTime || !durationMinutes) return info('Не хватает данных для встречи.');
        const body = consultationPayload(
          draft.client,
          date,
          startTime,
          plusMinutes(startTime, durationMinutes),
          consultationTimeZone,
          undefined,
          draft.intent.operation === 'create' ? draft.intent.recurrence : undefined,
        );
        await request(consultationService + '/consultations/availability', {
          method: 'POST',
          body: JSON.stringify({
            ...body,
            ...(draft.consultation ? { consultation_id: String(draft.consultation.id), scope: draft.scope } : {}),
          }),
        });
        let saved: Consultation;
        if (draft.consultation) {
          const scopeQuery = draft.scope ? '?scope=' + draft.scope : '';
          saved = await request<Consultation>(
            consultationService + '/consultations/' + draft.consultation.id + scopeQuery,
            { method: 'PUT', body: JSON.stringify(body) },
          );
        } else {
          const code = await request<{ code: string }>(consultationService + '/consultations/generate-code');
          saved = await request<Consultation>(consultationService + '/consultations', {
            method: 'POST',
            body: JSON.stringify({
              ...body,
              code: code.code,
              link: window.location.origin + '/meet/' + code.code,
              is_group: false,
              max_participants: 3,
            }),
          });
        }
        dispatch(consultationsApi.util.invalidateTags(['Consultations']));
        drafts.current.delete(draftId);
        if (!draft.consultation && saved.link) {
          latestCreatedMeeting.current = { link: saved.link, clientName: draft.client.full_name };
        }
        const scheduledTime = formatTime(saved.datetime_start, consultationTimeZone) || date + ', ' + startTime;
        const link = meetingLink(saved.link);
        return {
          kind: 'list',
          title: draft.consultation ? 'Встреча перенесена' : 'Встреча создана',
          items: [
            {
              id: String(saved.id),
              title: scheduledTime,
              description: 'Клиент: ' + draft.client.full_name,
              entityKind: 'consultation',
              ...(link
                ? {
                    href: link,
                    invitationText: invitationText(scheduledTime, link),
                  }
                : {}),
            },
          ],
        };
      } catch {
        return info('Не удалось выполнить действие: расписание могло измениться. Проверьте данные и попробуйте снова.');
      }
    },
    [consultationTimeZone, dispatch, preview, request],
  );

  const dismiss = useCallback((draftId?: string) => {
    if (draftId) drafts.current.delete(draftId);
  }, []);

  return { prepare, select, confirm: execute, dismiss };
}

function info(message: string): SupportMeetingWorkflowResult {
  return { kind: 'info', message };
}
function confirm(draftId: string, confirmLabel: string, message: string): SupportMeetingWorkflowResult {
  return { kind: 'confirmation', draftId, confirmLabel, message };
}
function items<T>(value: Collection<T>): T[] {
  return Array.isArray(value) ? value : value.items || value['hydra:member'] || [];
}
function normalName(value: string) {
  return value.trim().toLocaleLowerCase().replace(/ё/g, 'е');
}
function availabilityErrorMessage(error: unknown) {
  if (error instanceof SupportWorkflowRequestError) {
    if (error.status === 400 || error.status === 409) {
      return 'Это время уже занято. Выберите другое время.';
    }
    if (error.status === 401 || error.status === 403) {
      return 'Не удалось проверить расписание: требуется заново войти в платформу.';
    }
  }
  return 'Не удалось проверить свободное время. Это не означает, что оно занято. Попробуйте ещё раз.';
}
function clientId(value?: string | null) {
  return value?.match(/\/clients\/(\d+)$/)?.[1] || '';
}
function meetingLink(link?: string | null) {
  if (!link) return undefined;
  try {
    const url = new URL(link, window.location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}
function invitationText(time: string, link: string) {
  return 'Здравствуйте! Ваша видеоконсультация состоится ' + time + '. Подключиться можно по ссылке: ' + link;
}
function plusMinutes(time: string, minutes: number) {
  const [hours, mins] = time.split(':').map(Number);
  const total = (hours * 60 + mins + minutes) % 1440;
  return String(Math.floor(total / 60)).padStart(2, '0') + ':' + String(total % 60).padStart(2, '0');
}
function addDays(date: string, days: number) {
  const value = new Date(date + 'T12:00:00Z');
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}
function consultationPayload(
  client: Client,
  date: string,
  start: string,
  end: string,
  timeZone: string,
  consultationId?: number,
  recurrence?: Intent['recurrence'],
) {
  return {
    client_entity: '/api/v1/clients/' + client.id,
    date,
    datetime_start: start,
    datetime_end: end,
    client_timezone: client.time_zone || timeZone,
    consultation_timezone: timeZone,
    consultation_type: 'online',
    ...(consultationId ? { consultation_id: String(consultationId) } : {}),
    ...(recurrence ? { recurrence } : {}),
  };
}

function selectSeriesScopeOrPreview(
  draftId: string,
  draft: Draft,
  preview: (draftId: string) => Promise<SupportMeetingWorkflowResult>,
): Promise<SupportMeetingWorkflowResult> {
  if (!draft.consultation?.series_id || draft.scope) return preview(draftId);
  return Promise.resolve({
    kind: 'selection',
    draftId,
    message:
      draft.intent.operation === 'delete'
        ? 'Это повторяющаяся встреча. Что удалить?'
        : 'Это повторяющаяся встреча. К каким встречам применить перенос?',
    choices: [
      { id: 'scope:single', label: 'Только эту встречу' },
      { id: 'scope:this_and_future', label: 'Эту и последующие' },
      { id: 'scope:series', label: 'Все будущие встречи серии' },
    ],
  });
}

function scopeLabel(scope: Scope) {
  return scope === 'single'
    ? 'только эта встреча'
    : scope === 'this_and_future'
      ? 'эта и последующие встречи'
      : 'все будущие встречи серии';
}

function recurrenceSummary(
  recurrence: Intent['recurrence'] | undefined,
  count: number,
  first: string,
  last: string,
  consultationTimeZone: string,
) {
  if (!recurrence) return count + ' встреч';
  const unit = recurrence.type === 'day' ? 'день' : recurrence.type === 'week' ? 'неделю' : 'месяц';
  const interval =
    recurrence.interval && recurrence.interval > 1
      ? ' каждые ' + recurrence.interval + ' ' + unit
      : recurrence.type === 'week'
        ? 'еженедельно'
        : recurrence.type === 'day'
          ? 'ежедневно'
          : 'ежемесячно';
  return (
    interval +
    ', ' +
    count +
    ' встреч: с ' +
    (formatTime(first, consultationTimeZone) || first) +
    ' по ' +
    (formatTime(last, consultationTimeZone) || last)
  );
}

function hasTimeZone(value?: string) {
  return !!value && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
}

function scheduleDate(value?: string) {
  if (!value) return null;
  return hasTimeZone(value) ? new Date(value) : new Date(value.replace(' ', 'T') + 'Z');
}
function schedulingDate(value: Date) {
  return value.toISOString().slice(0, 10);
}
function schedulingTime(value: Date) {
  return value.toISOString().slice(11, 16);
}
function formatTime(value?: string, consultationTimeZone?: string) {
  const date = scheduleDate(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  return formatDateTime(date, hasTimeZone(value) ? consultationTimeZone : 'UTC');
}
function formatClientTime(value: string | undefined, clientZone: string | null | undefined, psychologistZone: string) {
  const date = scheduleDate(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  if (hasTimeZone(value)) {
    const formatted = formatDateTime(date, clientZone);
    if (formatted) return formatted;
  }
  const clientOffset = offsetMinutes(clientZone, date);
  const psychologistOffset = offsetMinutes(psychologistZone, date);
  if (clientOffset === null || psychologistOffset === null) return null;
  return formatDateTime(new Date(date.getTime() + (clientOffset - psychologistOffset) * 60_000), 'UTC');
}
function formatDateTime(date: Date, timeZone: string | null | undefined) {
  try {
    return date.toLocaleString('ru-RU', { timeZone: timeZone || 'UTC', dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return null;
  }
}
function offsetMinutes(zone: string | null | undefined, date: Date): number | null {
  const fixed = zone?.match(/\(GMT\s*([+-])(\d{2}):(\d{2})\)/);
  if (fixed) return (fixed[1] === '-' ? -1 : 1) * (Number(fixed[2]) * 60 + Number(fixed[3]));
  if (!zone) return null;
  try {
    const raw = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;
    const match = raw?.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    return match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3] || 0)) : 0;
  } catch {
    return null;
  }
}
